import { useCancelWithdrawalOrderMutation, useFetchWithdrawalOrdersQuery } from 'rtkQuery/sharespro/queries/portfolio';
import useQueryParam from 'hooks/shared/useQueryParam';
import { t } from 'i18next';
import PortfolioPageLayout from 'presentation/layout/modules/portfolio/PortfolioPageLayout';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import AppCard from 'presentation/components/shared/card/AppCard';
import React, { FC } from 'react';
import PaginationContentWrap from 'presentation/components/shared/pagination/PaginationContentWrap';
import { Button, Center, Flex, Icon, IconButton, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useToast } from '@chakra-ui/react';
import moment from 'moment';
import { WithdrawalOrder, WithdrawalOrderStatus } from 'model/modules/withdrawal/withdrawalOrder.t';
import { ButtonSize, ButtonVariant } from 'presentation/theme/components/button';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import useToastError from 'hooks/shared/useToastError';
import { MdContentCopy, MdAccountBalanceWallet } from 'react-icons/md';
import { Color } from 'presentation/theme/colorScheme';

const WithdrawalOrderCancelButton: FC<{ id: number; onCanceled: (canceledOrder: WithdrawalOrder) => void }> = (props) => {
  const [cancelOrder, { isLoading }] = useCancelWithdrawalOrderMutation();

  const toastErrorHandler = useToastError();
  const toast = useToast();

  return (
    <Button variant={ButtonVariant.Danger}
            isLoading={isLoading}
            size={ButtonSize.sm}
            onClick={() => {
              cancelOrder(props.id)
                .unwrap()
                .then((order) => {
                  props.onCanceled(order);
                  toast({
                    title: t('orderCanceled'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                  });
                })
                .catch((err) => toastErrorHandler(err));
            }}>
        {t('cancelAction')}
    </Button>
  );
};

const WithdrawalOrdersPage: FC = (_props) => {
  const [page, setPage] = useQueryParam<number>('page', {
    ifAbsent: 1,
    provider: (val) => parseInt(val),
  });
  const [accountId] = useQueryParam('account_id', {
    ifAbsent: undefined,
    provider: (val) => parseInt(val),
  });
  const { data, isFetching, error, refetch } = useFetchWithdrawalOrdersQuery({
    page,
    account_id: accountId,
  });

  const orderStatusText = (status: WithdrawalOrderStatus): string => {
    switch (status) {
      case WithdrawalOrderStatus.canceled:
        return t('orderStatusCanceled');
      case WithdrawalOrderStatus.processing:
        return t('requestProcessing');
      case WithdrawalOrderStatus.rejected:
        return t('requestRejected');
      case WithdrawalOrderStatus.successfully:
        return t('requestDone');
      case WithdrawalOrderStatus.waiting:
        return t('requestWaitingProcessing');
      case WithdrawalOrderStatus.unknown:
      default:
        return '';
    }
  };

  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
  };

  const renderWalletAddress = (address: string | null): JSX.Element | null => {
    if (!address) {
      return <Text color="gray.500" fontSize="sm">—</Text>;
    }

    const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

    return (
      <Flex align="center" gap={2}>
        <Icon as={MdAccountBalanceWallet} color={Color.Primary200} boxSize={5} />
        <Tooltip label={address} placement="top">
          <Text fontSize="sm" fontFamily="monospace">
            {shortAddress}
          </Text>
        </Tooltip>
        <IconButton
          aria-label="Copy address"
          icon={<MdContentCopy />}
          size="m"
          color={Color.Primary200}
          variant="ghost"
          onClick={() => copyToClipboard(address)}
        />
      </Flex>
    );
  };

  let content;

  if (data) {
    content = <PaginationContentWrap itemsCount={data.items.length}
                                     pageCount={data.pageCount}
                                     currentPage={data.currentPage}
                                     setPage={setPage}
                                     isFetching={isFetching}>
      <TableContainer
        pb={'10px'}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>{t('withdrawalOrderDate')}</Th>
              <Th>ID</Th>
              <Th>{t('withdrawalOrderAmount')}</Th>
              <Th>{t('walletAddress')}</Th>
              <Th>{t('orderStatus')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {
              data.items.map((item) => (
                <Tr key={item.id}>
                  <Td>{moment(item.created_at * 1000).format('DD.MM.YYYY')}</Td>
                  <Td>№{item.id}</Td>
                  <Td>{item.amount}{item.currency.symbol}</Td>
                  <Td>{renderWalletAddress(item.wallet_address)}</Td>
                  <Td>{orderStatusText(item.status)}</Td>
                  <Td>
                    {item.is_cancelable
                      ? <WithdrawalOrderCancelButton id={item.id}
                                                     onCanceled={() => {
                                                       refetch();
                                                     }}
                      /> : null}
                  </Td>
                </Tr>
              ))
            }
          </Tbody>
        </Table>
      </TableContainer>
    </PaginationContentWrap>;
  } else if (isFetching) {
    content = <Center><Spinner /></Center>;
  } else if (error) {
    content = (
      <ErrorContainer
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  } else {
    content = <Center><Spinner /></Center>;
  }

  return (
    <>
      <PortfolioPageLayout>
        <GridHeading>{t('withdrawalRequests')}</GridHeading>
        <AppCard pl={'20px'}
                 pr={'20px'}
                 pb={'10px'}
                 pt={'10px'}
        >
          {content}
        </AppCard>
      </PortfolioPageLayout>
    </>
  );
};

export default WithdrawalOrdersPage;
